import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowDownToLine, Banknote, Flame, Wallet } from "lucide-react";
import { toast } from "sonner";
import { analytics, takePendingCheckout } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  getWalletOverview,
  requestWithdrawal,
  verifyCheckoutSession,
  MIN_WITHDRAWAL_CENTS,
  PAYOUT_CURRENCIES,
  type PayoutCurrency,
} from "@/lib/wallets.functions";
import { formatHeat, formatUsd } from "@/lib/behot";
import { TopupPanel } from "@/components/topup-panel";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/wallets")({
  component: WalletsPage,
  head: () =>
    privatePageMetadata(
      "/app/wallets",
      "Wallets | BeHot.Now",
      "Top-up, Earn and Reward wallets.",
    ),
});

function WalletsPage() {
  const { t } = useT();
  const queryClient = useQueryClient();
  const overviewFn = useServerFn(getWalletOverview);
  const withdrawFn = useServerFn(requestWithdrawal);
  const verifyFn = useServerFn(verifyCheckoutSession);
  const verified = useRef(false);

  // Safety net: confirm the payment on return from Stripe in case the webhook is delayed.
  useEffect(() => {
    if (verified.current) return;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return;
    verified.current = true;
    void verifyFn({ data: { sessionId } })
      .then((res) => {
        if (res.ok) {
          toast.success(t("dashboard.wallets.topupConfirmed"));
          const pending = takePendingCheckout();
          analytics.purchase({
            transactionId: sessionId,
            kind: pending?.kind ?? "topup",
            valueUsd: pending?.valueUsd ?? 0,
          });
          void queryClient.invalidateQueries({ queryKey: ["wallet-overview"] });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        window.history.replaceState({}, "", window.location.pathname);
      });
  }, [verifyFn, queryClient, t]);

  const [withdrawUsd, setWithdrawUsd] = useState("");
  const [withdrawCurrency, setWithdrawCurrency] = useState<PayoutCurrency>("USD");
  const [methodId, setMethodId] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["wallet-overview"],
    queryFn: () => overviewFn(),
  });

  const methods = data?.methods ?? [];
  const activeMethodId = methodId || methods[0]?.id || "";

  const withdraw = useMutation({
    mutationFn: async () => {
      const usd = Number(withdrawUsd);
      if (!Number.isFinite(usd)) throw new Error(t("dashboard.wallets.errorEnterAmount"));
      if (!activeMethodId) throw new Error(t("dashboard.wallets.errorAddPayoutMethod"));
      // Internal balances are stored in AED cents; the withdrawal is entered and paid in USD.
      const amountCents = Math.round(usd * 433);
      if (amountCents < MIN_WITHDRAWAL_CENTS) {
        throw new Error(t("dashboard.wallets.errorMinWithdrawal", { amount: MIN_WITHDRAWAL_CENTS / 433 }));
      }
      return withdrawFn({
        data: { payoutMethodId: activeMethodId, amountCents, currency: withdrawCurrency },
      });
    },
    onSuccess: () => {
      toast.success(t("dashboard.wallets.withdrawalRequested"));
      setWithdrawUsd("");
      void queryClient.invalidateQueries({ queryKey: ["wallet-overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const balances = data?.balances;
  const cards = useMemo(
    () => [
      {
        key: "topup",
        icon: Wallet,
        title: t("dashboard.wallets.heatWallet"),
        value: balances ? t("dashboard.wallets.heatValue", { amount: formatHeat(balances.topupCents) }) : t("dashboard.wallets.na"),
        sub: t("dashboard.wallets.heatSub"),
        note: t("dashboard.wallets.heatNote"),
      },
      {
        key: "earn",
        icon: Banknote,
        title: t("dashboard.wallets.earnWallet"),
        value: balances ? formatUsd(balances.earnCents) : t("dashboard.wallets.na"),
        sub: t("dashboard.wallets.earnSub"),
        note: t("dashboard.wallets.earnNote"),
      },
      {
        key: "referral",
        icon: Flame,
        title: t("dashboard.wallets.referralWallet"),
        value: balances ? t("dashboard.wallets.heatValue", { amount: formatHeat(balances.referralCents) }) : t("dashboard.wallets.na"),
        sub: t("dashboard.wallets.referralSub"),
        note: t("dashboard.wallets.referralNote"),
      },
    ],
    [balances, t],
  );

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.key} className="rounded-xl border bg-card p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <c.icon className="size-4" /> {c.title}
            </div>
            <p className="mt-3 font-display text-2xl font-bold">
              {isLoading ? "…" : c.value}
            </p>
            {c.sub && <p className="text-xs text-muted-foreground">{c.sub}</p>}
            <p className="mt-3 text-xs text-muted-foreground">{c.note}</p>
          </div>
        ))}
      </div>

      <section className="rounded-xl border bg-card p-5">
        <h2 className="font-display text-lg font-bold">{t("dashboard.wallets.topupHeading")}</h2>
        <p className="mt-1 mb-4 text-sm text-muted-foreground">
          {t("dashboard.wallets.topupDesc")}
        </p>
        <TopupPanel />
      </section>


      <section className="rounded-xl border bg-card p-5">
        <h2 className="font-display text-lg font-bold">{t("dashboard.wallets.withdrawHeading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("dashboard.wallets.withdrawDesc", { amount: formatUsd(MIN_WITHDRAWAL_CENTS) })}
        </p>


        {methods.length === 0 ? (
          <p className="mt-4 text-sm">
            {t("dashboard.wallets.noPayoutMethod")}{" "}
            <Link to="/app/profile" className="font-semibold text-primary underline">
              {t("dashboard.wallets.addAndVerify")}
            </Link>
            .
          </p>
        ) : (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={activeMethodId}
              onChange={(e) => setMethodId(e.target.value)}
            >
              {methods.map((m) => (
                <option key={m.id} value={m.id}>
                  {t("dashboard.wallets.bankOption", { detail: m.iban?.slice(-6) ?? m.bank_name ?? "" })}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">US$</span>
              <Input
                className="w-32"
                inputMode="decimal"
                placeholder="0.00"
                value={withdrawUsd}
                onChange={(e) => setWithdrawUsd(e.target.value)}
              />
              <select
                className="h-10 rounded-md border bg-background px-3 text-sm"
                value={withdrawCurrency}
                onChange={(e) => setWithdrawCurrency(e.target.value as PayoutCurrency)}
                aria-label={t("dashboard.wallets.transferCurrency")}
              >
                {PAYOUT_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={() => withdraw.mutate()} disabled={withdraw.isPending}>
              <ArrowDownToLine className="size-4" />
              {withdraw.isPending ? t("dashboard.wallets.requesting") : t("dashboard.wallets.requestPayout")}
            </Button>
          </div>
        )}

        {(data?.withdrawals?.length ?? 0) > 0 && (
          <div className="mt-6 -mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-[520px] text-sm">

            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2">{t("dashboard.wallets.colDate")}</th>
                <th>{t("dashboard.wallets.colAmount")}</th>
                <th>{t("dashboard.wallets.colCurrency")}</th>
                <th>{t("dashboard.wallets.colStatus")}</th>
                <th>{t("dashboard.wallets.colReference")}</th>
              </tr>
            </thead>
            <tbody>
              {data!.withdrawals.map((w) => (
                <tr key={w.id} className="border-t">
                  <td className="py-2">{new Date(w.created_at).toLocaleDateString()}</td>
                  <td>{formatUsd(Number(w.amount_cents))}</td>
                  <td>{w.currency ?? "USD"}</td>
                  <td className="capitalize">{w.status}</td>
                  <td className="text-muted-foreground">{w.reference ?? t("dashboard.wallets.na")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

      </section>
    </div>
  );
}
